/**
 * Formats a US phone number to (XXX) XXX-XXXX format
 * @param {string} value - The phone number string to format
 * @returns {string} - The formatted phone number
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');
  
  // Special case: 911 should remain as 911
  if (cleaned === '911') return '911';
  
  // If starts with 1 (US country code) and has more than 10 digits, remove the 1
  let workingNumber = cleaned;
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    workingNumber = cleaned.slice(1);
  } else if (cleaned.length > 11 && cleaned.startsWith('1')) {
    workingNumber = cleaned.slice(1, 11);
  } else {
    // Limit to 10 digits
    workingNumber = cleaned.slice(0, 10);
  }
  
  // Format based on length
  if (workingNumber.length <= 3) {
    return workingNumber;
  } else if (workingNumber.length <= 6) {
    return `(${workingNumber.slice(0, 3)}) ${workingNumber.slice(3)}`;
  } else {
    return `(${workingNumber.slice(0, 3)}) ${workingNumber.slice(3, 6)}-${workingNumber.slice(6)}`;
  }
};

/**
 * Validates a US phone number
 * @param {string} value - The phone number string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePhoneNumber = (value) => {
  if (!value) return false;
  
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');
  
  // Special case: 911 is valid
  if (cleaned === '911') return true;
  
  // Must be exactly 10 digits, or 11 digits starting with 1 (US country code)
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
};

/**
 * Gets the raw phone number (digits only)
 * @param {string} value - The formatted phone number
 * @returns {string} - The raw phone number digits
 */
export const getRawPhoneNumber = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

/**
 * Safely formats a phone number from database, handling old invalid entries
 * @param {string} value - The phone number string from database
 * @returns {string} - The formatted phone number if valid, or original value if invalid
 */
export const safeFormatPhoneNumber = (value) => {
  if (!value) return '';
  
  // If the value is already valid, format it
  if (validatePhoneNumber(value)) {
    return formatPhoneNumber(value);
  }
  
  // If invalid, return as-is (for old database entries)
  return value;
};
